import {
  TextField,
  InputAdornment,
  IconButton,
  Box,
  useTheme,
} from '@mui/material'
import React, { ChangeEvent, KeyboardEvent, useState } from 'react'
import close from '../assets/icons/closeIcon.svg'
import useCustomStyles from '../utils/hooks/CustomStylesHook'
import { keyConstants } from './constants'
import { emptyString } from '../features/Radiology/Constants/constants'

type Item = {
  label: string
  value: string
}

type Props = {
  items: Item[]
  onSearch: (query: string, param: string) => void
  placeHolder?: string
}
const styles = () => ({
  globalSearchInput: {
    minWidth: '300px !important',
    backgroundColor: 'white !important',
    fontSize: '10px',
    padding: '0px !important',
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#aeafb5',
      },
      '&:hover fieldset': {
        borderColor: '#aeafb5 !important',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#aeafb5 !important',
        borderWidth: '1px !important',
      },
    },
    '& .MuiInputBase-input': {
      padding: 'unset',
    },
    '& .MuiInputBase-adornedStart': {
      padding: '0px !important',
    },
  },
  categorySelector: {
    backgroundColor: '#4682b4',
    color: 'white',
    fontSize: '8px',
    padding: '1px 4px',
  },
  endInputAdormentContainer: {
    fontSize: '10px',
    marginRight: '10px !important',
  },
  closeIcon: {
    width: '12px',
    position: 'absolute',
    left: '10px',
    top: '3px',
  },
  optionsWrapper: {
    zIndex: 9999999,
    position: 'absolute',
    top: '30px',
    width: '400px',
    border: '1px solid #aeafb5',
    background: 'white',
  },
  selectOption: {
    padding: '4px',
    margin: '0',
    cursor: 'pointer',
    backgroundColor: '#ffffff',
    fontSize: '13px',
    '&:not(:last-child)': {
      borderBottom: '1px solid #aeafb5',
    },
    '& .active': {
      backgroundColor: '#004c91 !important',
      color: '#ffffff',
    },
  },
  active: {
    backgroundColor: '#004c91 !important',
    color: '#ffffff',
  },
  selectValue: {
    fontStyle: 'italic',
    fontWeight: 'bold',
  },
  globalSearchWrapper: {
    position: 'relative',
  },
})

const GlobalSearch = ({ items, onSearch }: Props) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const [inputValue, setInputValue] = useState<string>('')
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const handleOnInput = () => {
    setIsEditing(true)
  }

  const handleOnBlur = () => {
    setIsEditing(false)
  }

  const handleOnChange = (ev: ChangeEvent<HTMLInputElement>) => {
    setInputValue(ev.target.value)
    setIsEditing(true)
    setSelectedCategory('')
    if (!ev.target.value) {
      onSearch('', '')
    }
  }

  const handleSelect = (index: number) => {
    const selectedLabel = items[index]?.label
    if (!selectedLabel) return

    setSelectedCategory(selectedLabel)
    setIsEditing(false)
    setActiveIndex(0)
    onSearch(selectedLabel, inputValue)
  }

  const handleKeyDown = (ev: KeyboardEvent<HTMLInputElement>) => {
    const size = items.length
    switch (ev.key) {
      case keyConstants?.tab:
      case keyConstants?.enter:
        if (items[activeIndex]) {
          handleSelect(activeIndex)
        }
        ev.preventDefault()
        break
      case keyConstants?.arrowUp:
        setActiveIndex((ai) => (ai + size - 1) % size)
        ev.preventDefault()
        break
      case keyConstants?.arrowDown:
        setActiveIndex((ai) => (ai + 1) % size)
        break
    }
  }
  const resetToInitial = () => {
    setInputValue('')
    setSelectedCategory('')
    onSearch('', '')
    setIsEditing(true)
  }
  return (
    <div className={classes?.globalSearchWrapper}>
      <TextField
        value={inputValue}
        placeholder='Search ....'
        className={classes?.globalSearchInput}
        onBlur={handleOnBlur}
        onClick={handleOnInput}
        onChange={handleOnChange}
        onKeyDown={handleKeyDown}
        fullWidth
        size='small'
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              {selectedCategory && (
                <Box className={classes?.categorySelector}>
                  {selectedCategory}
                </Box>
              )}
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position='end'>
              {inputValue && (
                <IconButton
                  edge='end'
                  className={classes?.endInputAdormentContainer}
                  onClick={resetToInitial}
                >
                  {inputValue && (
                    <img src={close} className={classes?.closeIcon} />
                  )}
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />

      {isEditing && inputValue && (
        <div className={classes?.optionsWrapper}>
          {items?.map((item, idx) => (
            <p
              key={idx}
              onMouseEnter={() => setActiveIndex(idx)}
              onMouseDown={() => handleSelect(idx)}
              className={`${classes?.selectOption} ${
                idx === activeIndex ? classes?.active : emptyString
              }`}
            >
              Search <strong>{item.label}</strong> for
              <span className={classes?.selectValue}>{inputValue}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  )
}

export default GlobalSearch
